import { Injectable, inject } from "@angular/core";
import { initProductHeaderFooter, LOGOUT_EVENT_NAME } from "@storeblocks/header-footer";
import { SURVEY_IN_SMART_PENSION } from "src/app/constants/technical.constants";
import { HotjarService } from "src/app/services/hotjar.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class MenuFooterService {
  private readonly hotjarService = inject(HotjarService);

  private initialized: boolean = false;

  public async startHeaderFooter(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const headerFooterEnvironment = environment.production ? "production" : "test";
    await initProductHeaderFooter(headerFooterEnvironment);
    this.attachLogoutSurveyTrigger();
    this.initialized = true;
  }

  /**
   * Set the Hotjar logged-out survey cookie when the user logs out via the product header.
   *
   * As of @storeblocks/header-footer 2.6.0 the library dispatches a `LOGOUT_EVENT_NAME` custom event
   * on `window` when the logout button is clicked — the supported integration point. This replaces
   * the previous approach of intercepting the click by CSS selector (`.hf-web-logoutButton`), which
   * depended on the library's internal DOM staying stable.
   *
   * We do not call `preventDefault`, so the library's logout redirect proceeds normally. The cookie
   * is written synchronously, before the redirect. We don't need the event payload.
   */
  private attachLogoutSurveyTrigger(): void {
    window.addEventListener(LOGOUT_EVENT_NAME, () => {
      if (this.shouldShowLogoutSurvey()) {
        this.hotjarService.setLogoutSurveyCookie();
      }
    });
  }

  /**
   * Suppress the logged-out survey when an in-app survey is already running (legacy behaviour:
   * no survey cookie when `hasSurveyInSmartPension`). Read from the URL directly to avoid coupling
   * this root service to the Angular router.
   */
  private shouldShowLogoutSurvey(): boolean {
    const surveyParam = new URLSearchParams(window.location.search).get(SURVEY_IN_SMART_PENSION.key);
    return surveyParam !== SURVEY_IN_SMART_PENSION.value;
  }
}
